/**
 * Shared URL handling for message text.
 *
 * Message and prompt text is authored by hand in the CMS, so admins routinely
 * type `www.google.com` rather than the full `https://www.google.com`. Both
 * forms are linkified; only the href gains the implied scheme, so the link text
 * still renders exactly as it was written.
 */

/** Matches a scheme-less `www.` URL, which carries an implied https:// scheme. */
const BARE_WWW_PREFIX = /^www\./i;

/**
 * Splits message text around absolute http(s) URLs and scheme-less `www.` URLs.
 * The `\b` keeps `www.` from matching mid-token (e.g. inside `subwww.foo`).
 *
 * Safe to share between calls: `String.prototype.split` does not carry
 * `lastIndex` the way `RegExp.prototype.test` does.
 */
export const MESSAGE_URL_SPLIT_REGEX = /((?:https?:\/\/|\bwww\.)[^\s]+)/gi;

/** Anchored, non-global counterpart of the split pattern. */
const FULL_MESSAGE_URL_REGEX = /^(?:https?:\/\/|www\.)[^\s]+$/i;

/**
 * Reports whether a part produced by splitting on MESSAGE_URL_SPLIT_REGEX is
 * itself a URL. Uses an anchored, non-global pattern so no `lastIndex` state
 * leaks between calls.
 *
 * @param {string} part - A part returned by the split.
 * @returns {boolean} True when the part is a linkable URL.
 */
export function isMessageURL(part) {
  return typeof part === 'string' && FULL_MESSAGE_URL_REGEX.test(part);
}

/**
 * Gives a scheme-less `www.` URL the implied `https://` scheme so it resolves
 * as an absolute URL rather than a path relative to the app's own origin. Any
 * other input is returned unchanged.
 *
 * @param {string} url - A URL token taken from message text.
 * @returns {string} The URL with an explicit http(s) scheme where one was implied.
 */
export function normalizeMessageURL(url) {
  if (typeof url !== 'string') {
    return url;
  }

  return BARE_WWW_PREFIX.test(url) ? `https://${url}` : url;
}
